import { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type KeywordFilterDropdownProps = {
  keywords: string[]
  value: string[]
  onChange: (next: string[]) => void
}

export const KeywordFilterDropdown = memo(function KeywordFilterDropdown({
  keywords,
  value,
  onChange,
}: KeywordFilterDropdownProps) {
  const { t } = useTranslation('feed')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  if (keywords.length === 0) return null

  const selectedCount = value.length

  const toggleKeyword = (kw: string) => {
    if (value.includes(kw)) {
      onChange(value.filter((v) => v !== kw))
    } else {
      onChange([...value, kw])
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-medium transition-colors',
          selectedCount > 0
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted/60',
        )}
      >
        <span>
          {selectedCount > 0
            ? t('keywordFilter.selected_other', { count: selectedCount })
            : t('keywordFilter.label')}
        </span>
        {selectedCount > 0 ? (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onChange([])
              }
            }}
            className="flex items-center rounded-full hover:text-foreground"
            aria-label={t('keywordFilter.clear')}
          >
            <X size={12} />
          </span>
        ) : (
          <ChevronDown
            size={12}
            className={cn('transition-transform', open && 'rotate-180')}
          />
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[160px] rounded-lg border border-border bg-card shadow-md">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('keywordFilter.label')}
            </span>
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  onChange([])
                  setOpen(false)
                }}
                className="text-[11px] text-primary hover:underline"
              >
                {t('keywordFilter.clear')}
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              onChange([])
              setOpen(false)
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-[13px] transition-colors hover:bg-muted/40',
              selectedCount === 0 ? 'font-medium text-primary' : 'text-foreground',
            )}
          >
            {t('keywordFilter.all')}
          </button>

          {keywords.map((kw) => {
            const checked = value.includes(kw)
            return (
              <button
                key={kw}
                type="button"
                onClick={() => toggleKeyword(kw)}
                className="flex w-full items-center gap-2 px-3 py-2 text-[13px] transition-colors hover:bg-muted/40"
              >
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border border-border',
                    checked && 'border-primary bg-primary',
                  )}
                >
                  {checked && (
                    <svg
                      viewBox="0 0 10 8"
                      className="h-2 w-2 fill-none stroke-white stroke-2"
                    >
                      <polyline points="1,4 4,7 9,1" />
                    </svg>
                  )}
                </span>
                <span
                  className={cn(
                    'truncate',
                    checked ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {kw}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
})
